import React, { useMemo, useCallback } from "react";
import type { AIEdit } from "../types/edit";
import { escapeForHtml, insertionIndex, replaceLiteral } from "../lib/edits";
import { MarkdownContent } from "./MarkdownContent";

interface PreviewPaneProps {
    markdown: string;
    activeEdits: AIEdit[];
    onAcceptEdit: (id: string) => void;
    onRejectEdit: (id: string) => void;
}

export function PreviewPane({ markdown, activeEdits, onAcceptEdit, onRejectEdit }: PreviewPaneProps) {
    const processedMarkdown = useMemo(() => {
        let text = markdown;

        // Process replacements first
        const replacements = activeEdits.filter(e => e.type === "replace" && e.original);
        for (const edit of replacements) {
            if (edit.original && text.includes(edit.original)) {
                const isMultiline = edit.original.includes('\n') || edit.new.includes('\n');
                const original = escapeForHtml(edit.original);
                const replacement = escapeForHtml(edit.new);

                let highlighted;
                if (isMultiline) {
                    highlighted = `
<div class="my-4 border border-base-300 rounded-lg p-4 relative group">
<div class="bg-error/10 text-error p-3 mb-2 rounded border-l-4 border-error">
<div class="text-xs font-bold uppercase mb-2 opacity-60">Removed</div>

${original}

</div>
<div class="bg-success/10 text-success p-3 rounded border-l-4 border-success">
<div class="text-xs font-bold uppercase mb-2 opacity-60">Added</div>

${replacement}

</div>
<div class="absolute -top-3 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-base-100 px-2 rounded shadow-sm border border-base-300">
    <button class="btn btn-xs btn-success cursor-pointer" aria-label="Accept" data-edit-action="accept" data-edit-id="${edit.id}">Accept</button>
    <button class="btn btn-xs btn-error btn-outline cursor-pointer" aria-label="Reject" data-edit-action="reject" data-edit-id="${edit.id}">Reject</button>
</div>
</div>
`;
                } else {
                    highlighted = `<span class="inline-flex items-center gap-1 mx-0.5 align-middle"><del class="bg-error/20 text-error no-underline px-1 rounded">${original}</del><ins class="bg-success/20 text-success no-underline px-1 rounded mr-1">${replacement}</ins><button class="btn btn-xs btn-circle btn-success cursor-pointer" aria-label="Accept" data-edit-action="accept" data-edit-id="${edit.id}"><svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg></button><button class="btn btn-xs btn-circle btn-error btn-outline cursor-pointer" aria-label="Reject" data-edit-action="reject" data-edit-id="${edit.id}"><svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg></button></span>`;
                }
                
                text = replaceLiteral(text, edit.original, highlighted);
            }
        }

        // Process insertions line by line in reverse order to avoid shifting issues
        const insertions = activeEdits.filter(e => e.type === "insert");
        if (insertions.length > 0) {
            const lines = text.split('\n');
            const sortedInsertions = [...insertions].sort((a, b) => (b.line || 0) - (a.line || 0));

            for (const edit of sortedInsertions) {
                if (edit.line !== undefined) {
                    const lineIdx = insertionIndex(edit.line, lines);
                    const highlighted = `
<div class="bg-success/10 text-success p-4 rounded my-4 border-l-4 border-success relative group">
<div class="text-xs font-bold uppercase mb-2 opacity-60">Inserted</div>

${escapeForHtml(edit.new)}

<div class="absolute -top-3 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-base-100 px-2 rounded shadow-sm border border-base-300">
    <button class="btn btn-xs btn-success cursor-pointer" aria-label="Accept" data-edit-action="accept" data-edit-id="${edit.id}">Accept</button>
    <button class="btn btn-xs btn-error btn-outline cursor-pointer" aria-label="Reject" data-edit-action="reject" data-edit-id="${edit.id}">Reject</button>
</div>
</div>
`;
                    lines.splice(lineIdx, 0, highlighted);
                }
            }
            text = lines.join('\n');
        }

        return text;
    }, [markdown, activeEdits]);

    const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const btn = target.closest('button[data-edit-action]');

        if (btn) {
            const action = btn.getAttribute('data-edit-action');
            const editId = btn.getAttribute('data-edit-id');

            if (editId) {
                if (action === 'accept') {
                    onAcceptEdit(editId);
                } else if (action === 'reject') {
                    onRejectEdit(editId);
                }
            }
        }
    }, [onAcceptEdit, onRejectEdit]);

    return (
        <div className="flex flex-col h-full bg-base-100">
            <div className="bg-base-200/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-base-content/50 flex justify-between items-center">
                <span>Live Preview</span>
                <span className="badge badge-sm badge-ghost">HTML</span>
            </div>
            <div
                className="flex-1 overflow-auto p-8"
                onClick={handleContainerClick}
            >
                <article className="prose prose-sm md:prose-base lg:prose-lg max-w-none prose-headings:font-bold prose-h1:text-4xl prose-pre:bg-base-300 prose-pre:text-base-content">
                    <MarkdownContent allowRaw>{processedMarkdown}</MarkdownContent>
                </article>
            </div>
        </div>
    );
}
